import { useCallback, useEffect, useState, type ReactNode, type UIEvent } from 'react';
import type { ListServersResponse, ListUsersResponse, MeResponse, ServerCap, ServerRecord, ServerView, UserRecord } from '@hearth/shared';
import { SERVER_CAP_MAX, serverHandle, serverName, sizeOf } from '@hearth/shared';
import type { Api } from '../api';
import { linkTo } from '../router';
import { Campfire } from '../ui/Campfire';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Avatar } from './Avatar';
import { campfireFor, errorText, statusText, timeAgo } from './serverWords';

// /app/admin (M10.17, admins): the env's server limit, everyone who has signed in, and every
// server, each list loading 25 more as it's scrolled near its end. The routes behind it refuse
// anyone else (twice: the User λ's admin gate, then each operation), so the page's own check only
// keeps others from seeing an empty shell.

/** Users or servers asked for at a time; scrolling near a list's end asks for the next page. */
export const ADMIN_PAGE = 25;

/** A listing as loaded so far, and where its next page starts (none: all loaded). */
interface Loaded<T> {
  items: T[];
  cursor: string | undefined;
}

/** The name to show: as the top bar does, then the email's name. */
export const personName = (u: UserRecord) => u.displayName ?? u.name ?? u.username ?? u.email?.split('@')[0] ?? 'Someone';
/** Google, Discord, or Password (a user made by the owner in Cognito). */
export const providerName = (u: UserRecord) => (!u.provider || u.provider === 'Cognito' ? 'Password' : u.provider);

/** A whole record as the server list words read it (an admin's view of any server). */
export function asView(s: ServerRecord): ServerView {
  return {
    serverId: s.serverId,
    name: serverName(s),
    handle: s.handle ?? serverHandle(serverName(s), s.serverId),
    role: 'admin',
    game: s.game,
    region: s.region,
    version: s.version,
    status: s.status,
    idleStopMinutes: s.idleStopMinutes ?? 30,
    restorePending: !!s.restoreKey,
    size: sizeOf(s),
    ...(s.playersOnline !== undefined ? { playersOnline: s.playersOnline } : {}),
    ...(s.lastPlayedAt ? { lastPlayedAt: s.lastPlayedAt } : {}),
  };
}

/** The limit typed, as a number the API takes (a whole number 0–SERVER_CAP_MAX), else undefined. */
export function capValue(draft: string): number | undefined {
  if (!/^\d+$/.test(draft.trim())) return undefined;
  const n = Number(draft.trim());
  return n <= SERVER_CAP_MAX ? n : undefined;
}

/** What saving a new limit means, for the dialog: lowering it below today's count refuses only new servers. */
export function capChangeText(to: number, count: number): string {
  return to < count
    ? `That's fewer than the ${count} servers Hearth has now. They keep running and waking; only new servers are refused until there are fewer than ${to}.`
    : `People can create servers until Hearth has ${to}. Admins can always create.`;
}

const pageQuery = (cursor: string | undefined) => `?limit=${ADMIN_PAGE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;

export function AdminPage({ api, me }: { api: Api; me: MeResponse }) {
  const [users, setUsers] = useState<Loaded<UserRecord>>();
  const [servers, setServers] = useState<Loaded<ServerRecord>>();
  const [loading, setLoading] = useState<'users' | 'servers'>();
  const [cap, setCap] = useState<ServerCap>();
  const [draft, setDraft] = useState('');
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const usersPage = useCallback(async (cursor: string | undefined): Promise<Loaded<UserRecord>> => {
    const r = await api.get<ListUsersResponse>(`/v1/admin/users${pageQuery(cursor)}`);
    return { items: r.users, cursor: r.cursor };
  }, [api]);
  const serversPage = useCallback(async (cursor: string | undefined): Promise<Loaded<ServerRecord>> => {
    const r = await api.get<ListServersResponse>(`/v1/admin/servers${pageQuery(cursor)}`);
    return { items: r.servers, cursor: r.cursor };
  }, [api]);
  const showCap = (c: ServerCap) => {
    setCap(c);
    setDraft(String(c.cap));
  };

  // Only admins ask (the API would refuse anyone else anyway).
  useEffect(() => {
    if (!me.admin) return;
    Promise.all([usersPage(undefined), serversPage(undefined), api.get<ServerCap>('/v1/admin/server-cap')]).then(
      ([u, s, c]) => {
        setUsers(u);
        setServers(s);
        showCap(c);
      },
      (err: unknown) => setError(errorText(err)),
    );
  }, [api, me.admin, usersPage, serversPage]);

  /** A listing's next page, added to what's shown (one at a time). */
  async function more<T>(which: 'users' | 'servers', loaded: Loaded<T> | undefined, page: (cursor: string | undefined) => Promise<Loaded<T>>, set: (l: Loaded<T>) => void) {
    if (!loaded?.cursor || loading) return;
    setLoading(which);
    try {
      const next = await page(loaded.cursor);
      set({ items: [...loaded.items, ...next.items], cursor: next.cursor });
      setError('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(undefined);
    }
  }

  async function saveCap() {
    const to = capValue(draft);
    if (to === undefined) return;
    setSaving(true);
    try {
      showCap(await api.patch<ServerCap>('/v1/admin/server-cap', { cap: to }));
      setError('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
      setAsking(false);
    }
  }

  if (!me.admin) {
    return (
      <div className="card">
        <h1>Admins only</h1>
        <a {...linkTo('/app')}>Go to your servers</a>
      </div>
    );
  }
  const to = capValue(draft);
  return (
    <>
      <h1 className="page-title">Admin</h1>
      {error && <p className="notice" role="alert">{error}</p>}
      {cap && <ServerLimitPanel cap={cap} draft={draft} onDraft={setDraft} onSave={() => setAsking(true)} />}
      <AdminView
        users={users?.items}
        servers={servers?.items}
        moreUsers={!!users?.cursor}
        moreServers={!!servers?.cursor}
        loading={loading}
        meId={me.userId}
        now={new Date()}
        onMoreUsers={() => void more('users', users, usersPage, setUsers)}
        onMoreServers={() => void more('servers', servers, serversPage, setServers)}
      />
      <ConfirmDialog
        open={asking}
        title={`Allow up to ${to ?? ''} servers on Hearth?`}
        confirmLabel="Save"
        tone="ember"
        busy={saving}
        onConfirm={() => void saveCap()}
        onCancel={() => setAsking(false)}
      >
        {cap && to !== undefined ? capChangeText(to, cap.count) : ''}
      </ConfirmDialog>
    </>
  );
}

/** The env's server limit: how many count now, and a field to change it (Save asks first). */
export function ServerLimitPanel({ cap, draft, onDraft, onSave }: { cap: ServerCap; draft: string; onDraft: (draft: string) => void; onSave: () => void }) {
  const to = capValue(draft);
  return (
    <section className="panel admin-cap">
      <h2>Server limit</h2>
      <div className="admin-cap-row">
        <div className="admin-cap-now">
          <strong>{`${cap.count} of ${cap.cap} servers`}</strong>
          <span className="quiet admin-meta">New servers are refused once Hearth reaches the limit. Admins can still create. Set 0 to pause new servers.</span>
        </div>
        <label className="quiet admin-meta" htmlFor="server-limit">Limit</label>
        <input
          id="server-limit"
          className="field admin-cap-field"
          type="number"
          inputMode="numeric"
          min={0}
          max={SERVER_CAP_MAX}
          value={draft}
          aria-invalid={to === undefined}
          onChange={(e) => onDraft(e.currentTarget.value)}
        />
        <button className="btn btn-primary" type="button" disabled={to === undefined || to === cap.cap} onClick={onSave}>
          Save
        </button>
      </div>
      {cap.count >= cap.cap && <p className="admin-cap-full">Hearth is full: only admins can create servers right now.</p>}
    </section>
  );
}

/** A list in its own scrolling box: nearing the end asks for the next page, if there is one. */
function ScrollList({ label, more, loading, onMore, children }: { label: string; more: boolean; loading: boolean; onMore: () => void; children: ReactNode }) {
  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const box = e.currentTarget;
    if (more && !loading && box.scrollTop + box.clientHeight >= box.scrollHeight - 48) onMore();
  };
  return (
    // Focusable, so the keyboard can scroll it (and so load more).
    <div className="admin-list" tabIndex={0} role="region" aria-label={label} onScroll={onScroll}>
      {children}
      {loading && (
        <p className="admin-list-note quiet" aria-live="polite">{`Loading more ${label.toLowerCase()}…`}</p>
      )}
    </div>
  );
}

/** The admin page's lists, given what's loaded (no fetching: easy to test). */
export function AdminView(props: {
  users: UserRecord[] | undefined;
  servers: ServerRecord[] | undefined;
  /** Whether each listing has more pages. */
  moreUsers: boolean;
  moreServers: boolean;
  /** Which list is loading its next page. */
  loading?: 'users' | 'servers' | undefined;
  meId: string;
  now: Date;
  onMoreUsers: () => void;
  onMoreServers: () => void;
}) {
  const { users, servers, moreUsers, moreServers, loading, meId, now, onMoreUsers, onMoreServers } = props;
  if (!users || !servers) return <p className="quiet" aria-live="polite">Loading…</p>;
  // Counts are of what's loaded: "25+" while more pages remain.
  const count = (n: number, more: boolean) => `${n}${more ? '+' : ''}`;
  const owned = (userId: string) => servers.filter((s) => s.ownerId === userId).length;
  // The users route is a scan (unordered), so what's loaded is shown by name.
  const people = [...users].sort((a, b) => personName(a).localeCompare(personName(b)));
  const ownerName = (s: ServerRecord) => {
    const owner = users.find((u) => u.userId === s.ownerId);
    if (owner) return personName(owner);
    // Servers from before accounts have an admin's ARN as owner; anyone else isn't loaded yet.
    return s.ownerId.startsWith('arn:') ? 'an admin (before accounts)' : 'someone';
  };

  return (
    <>
      <section className="panel">
        <h2>{`Users (${count(people.length, moreUsers)})`}</h2>
        <ScrollList label="Users" more={moreUsers} loading={loading === 'users'} onMore={onMoreUsers}>
          <ul className="people">
            {people.map((u) => (
              <li key={u.userId}>
                <Avatar name={personName(u)} picture={u.picture} />
                <span className="person-name admin-who">
                  <span>
                    {personName(u)}
                    {u.userId === meId && <span className="quiet"> (you)</span>}
                  </span>
                  <span className="quiet admin-meta">
                    {[providerName(u), u.email, `last seen ${timeAgo(u.lastSeenAt, now)}`, owned(u.userId) === 1 ? '1 server' : `${owned(u.userId)} servers`]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </ScrollList>
      </section>
      <section className="panel">
        <h2>{`Every server (${count(servers.length, moreServers)})`}</h2>
        {servers.length === 0 ? (
          <p className="quiet">No servers yet.</p>
        ) : (
          <ScrollList label="Servers" more={moreServers} loading={loading === 'servers'} onMore={onMoreServers}>
            <ul className="people">
              {servers.map((s) => {
                const view = asView(s);
                return (
                  <li key={s.serverId}>
                    <span className="admin-fire"><Campfire state={campfireFor(view)} height={20} still /></span>
                    <span className="person-name admin-who">
                      <a {...linkTo(`/app/servers/${encodeURIComponent(s.serverId)}`)}>{view.name}</a>
                      <span className="quiet admin-meta">{`${ownerName(s)} · Minecraft ${s.version} · ${statusText(view, now)}`}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </ScrollList>
        )}
      </section>
    </>
  );
}
